import { gql } from '@apollo/client';
import React from 'react';
import GenericErrorBoundary from '../components/GenericErrorBoundary';
import InfiniteScrollWaypoint from '../components/InfiniteScrollWaypoint';
import type { Activities_ActivitiesFragment } from '../graphql/generated/graphqlTypes';
import ActivityCreatedComment from './ActivityCreatedComment';
import ActivityCreatedHistory from './ActivityCreatedHistory';
import ActivityJoinedUser from './ActivityJoinedUser';
import ActivityPublishedPost from './ActivityPublishedPost';
import ActivityRepliedComment from './ActivityRepliedComment';
import ActivityThankedPost from './ActivityThankedPost';

const ACTIVITIES_ACTIVITY = gql`
  fragment Activities_activities on Activity {
    actedAt
    actor {
      id
    }
    ... on PublishedPostActivity {
      ...ActivityPublishedPost_activity
    }
    ... on ThankedPostActivity {
      ...ActivityThankedPost_activity
    }
    ... on CreatedCommentActivity {
      ...ActivityCreatedComment_activity
    }
    ... on RepliedCommentActivity {
      ...ActivityRepliedComment_activity
    }
    ... on JoinedUserActivity {
      ...ActivityJoinedUser_activity
    }
    ... on CreatedHistoryActivity {
      ...ActivityCreatedHistory_activity
    }
  }
  ${ActivityPublishedPost.fragments.activity}
  ${ActivityThankedPost.fragments.activity}
  ${ActivityCreatedComment.fragments.activity}
  ${ActivityRepliedComment.fragments.activity}
  ${ActivityJoinedUser.fragments.activity}
  ${ActivityCreatedHistory.fragments.activity}
`;

interface ActivitiesProps {
  activities: Activities_ActivitiesFragment[];
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetchMore: (() => any) | null;
}

const renderSingleActivity = (activity: Activities_ActivitiesFragment) => {
  switch (activity.__typename) {
    case 'PublishedPostActivity':
      return <ActivityPublishedPost activity={activity} />;
    case 'ThankedPostActivity':
      return <ActivityThankedPost activity={activity} />;
    case 'CreatedCommentActivity':
      return <ActivityCreatedComment activity={activity} />;
    case 'RepliedCommentActivity':
      return <ActivityRepliedComment activity={activity} />;
    case 'JoinedUserActivity':
      return <ActivityJoinedUser activity={activity} />;
    case 'CreatedHistoryActivity':
      return <ActivityCreatedHistory activity={activity} />;
    default:
      return null;
  }
};

const Activities = ({ activities, fetchMore }: ActivitiesProps) => {
  const realActivities = activities.filter(
    (activity) => activity.__typename !== 'FillerActivity',
  );

  if (realActivities.length === 0 && !fetchMore) return null;

  return (
    <div className="activities">
      <div>
        {realActivities.map((activity) => (
          <GenericErrorBoundary
            key={`${activity.actor?.id || ''}-${activity.actedAt || ''}`}
            name="activity"
          >
            {renderSingleActivity(activity)}
          </GenericErrorBoundary>
        ))}
      </div>
      <InfiniteScrollWaypoint fetchMore={fetchMore} />
    </div>
  );
};

Activities.fragments = { activities: ACTIVITIES_ACTIVITY };

export default Activities;
